import { useState, useRef, useEffect } from 'react';
import {
  Editing,
  MasterDetail,
  Texts,
} from 'devextreme-react/data-grid';
import { SquireDataGrid } from '../squire-data-grid';

/**
 * Reusable component for managing one-to-many subordinate collections
 * using the SubordinateOneToManyObjectChanges pattern (toCreate/toUpdate/toDelete).
 *
 * Uses the DataGrid's built-in editing for adding, updating, and deleting rows.
 * Columns (including lookups for selection) are configured by the caller via gridColumns.
 *
 * @param {Object} props
 * @param {string} props.title - Section heading
 * @param {Function} props.onChange - Callback: (changes: { toCreate, toUpdate, toDelete }) => void
 * @param {Array} [props.initialItems=[]] - Pre-populated EXISTING items (baseline for edit mode; tracked for update/delete)
 * @param {Array} [props.initialNewItems=[]] - Pre-populated items seeded as NEW (flow through toCreate, e.g. a prefilled row)
 * @param {Array} props.gridColumns - DevExtreme Column elements for the grid (configure lookups on columns for selection)
 * @param {string} props.keyExpr - Key field for the DataGrid items
 * @param {string} [props.noDataText="No items added yet."] - DataGrid empty message
 * @param {string} [props.confirmDeleteMessage="Are you sure you want to remove this item?"] - Delete confirmation
 * @param {string} [props.editMode="row"] - DataGrid editing mode (row, batch, cell, popup, form)
 * @param {boolean} [props.autoExpandDetail=false] - expand every row's master-detail by default, so
 *   the detail (e.g. contacts) is visible without hunting for the expand arrow.
 * @param {boolean} [props.allowAdding=true] - show the grid's "add row" affordance.
 * @param {boolean} [props.allowUpdating=true] - allow in-row editing of existing rows.
 * @param {boolean} [props.allowDeleting=true] - show the per-row delete affordance.
 *   These three gate the DevExtreme editing toolbar; e.g. a singular role (landlord) hides adding
 *   once a row exists and never allows deleting the sole row.
 * @param {Object} [props.newRowDefaults] - field values applied to a new row on insert. Defaulting
 *   boolean columns (e.g. { primary: false }) avoids DevExtreme's indeterminate (dashed) checkbox.
 * @param {Function} [props.detailRender] - Master-detail render: (e, { updateItem }) => ReactNode.
 *   updateItem(changes) merges changes onto the expanded row. UI-only fields (keys prefixed with
 *   "_") are kept on the row for display but stripped from the toCreate/toUpdate wire payload.
 *
 * IMMEDIATE MODE: pass any of onCreateRow / onUpdateRow / onDeleteRow to persist each mutation as it
 * happens (instead of batching into onChange). The grid fires the callback and cancels its own edit;
 * the parent is expected to persist, then refetch and remount this editor (via a changing `key`) so
 * the rows reflect server truth. onChange / change-tracking is skipped in this mode.
 * @param {Function} [props.onCreateRow] - (rowData) => void; a new row was added
 * @param {Function} [props.onUpdateRow] - (key, rowData) => void; an existing row was edited
 * @param {Function} [props.onDeleteRow] - (key, rowData) => void; a row was removed
 */
// UI-only fields (keys prefixed with "_", e.g. _isNew or display-only data attached
// by a detail editor) are never sent to the server.
function stripInternalFields(item) {
  return Object.fromEntries(
    Object.entries(item).filter(([k]) => !k.startsWith('_'))
  );
}

function computeChanges(currentItems, originalItems, key) {
  const originalMap = new Map(originalItems.map((i) => [i[key], i]));
  const currentMap = new Map(currentItems.map((i) => [i[key], i]));

  const toCreate = currentItems
    .filter((i) => i._isNew)
    .map((i) => stripInternalFields(i));

  const toUpdate = {};
  for (const item of currentItems) {
    if (!item._isNew && originalMap.has(item[key])) {
      const orig = originalMap.get(item[key]);
      const changedFields = {};
      let hasChanges = false;
      for (const field of Object.keys(item)) {
        if (field.startsWith('_')) continue;
        if (item[field] !== orig[field]) {
          changedFields[field] = item[field];
          hasChanges = true;
        }
      }
      if (hasChanges) {
        toUpdate[String(item[key])] = JSON.stringify(changedFields);
      }
    }
  }

  const toDelete = originalItems
    .filter((orig) => !currentMap.has(orig[key]))
    .map((orig) => orig[key]);

  return { toCreate, toUpdate, toDelete };
}

export function SubordinateCollectionEditor({
  title,
  onChange,
  initialItems = [],
  initialNewItems = [],
  gridColumns,
  keyExpr,
  noDataText = 'No items added yet.',
  confirmDeleteMessage = 'Are you sure you want to remove this item?',
  editMode = 'row',
  detailRender,
  autoExpandDetail = false,
  allowAdding = true,
  allowUpdating = true,
  allowDeleting = true,
  newRowDefaults,
  onCreateRow,
  onUpdateRow,
  onDeleteRow,
}) {
  // Immediate mode fires per-mutation callbacks instead of accumulating toCreate/toUpdate/toDelete.
  const immediate = !!(onCreateRow || onUpdateRow || onDeleteRow);
  const [items, setItems] = useState(() => [
    ...(initialItems || []).map((i) => ({ ...i, _isNew: false })),
    ...(initialNewItems || []).map((i) => ({ ...i, _isNew: true })),
  ]);
  const originalItemsRef = useRef(
    (initialItems || []).map((i) => ({ ...i }))
  );
  const gridRef = useRef(null);


  // Held in a ref so the emit effect below does not need onChange as a dependency: parents
  // typically redeclare it every render, which would make the effect fire on every render and
  // emit -> parent setState -> render -> emit again.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    // In immediate mode persistence happens per-mutation, so there are no batched changes to emit.
    const emitChanges = onChangeRef.current;
    if (!immediate && emitChanges) {
      emitChanges(computeChanges(items, originalItemsRef.current, keyExpr));
    }
  }, [items, immediate, keyExpr]);

  function onRowInserting(e) {
    e.cancel = true;
    if (gridRef.current) {
      gridRef.current.instance().cancelEditData();
    }

    if (immediate) {
      // Parent persists then refetches + remounts; don't touch local state.
      if (onCreateRow) onCreateRow(stripInternalFields(e.data));
      return;
    }

    // Batch mode: add the row manually with the _isNew flag.
    setItems((prev) => [...prev, { ...e.data, _isNew: true }]);
  }

  function onRowUpdated(e) {
    if (immediate) {
      if (onUpdateRow) onUpdateRow(e.key, stripInternalFields(e.data));
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i[keyExpr] === e.key ? { ...i, ...e.data } : i))
    );
  }

  function updateItemByKey(key, changes) {
    setItems((prev) =>
      prev.map((i) => (i[keyExpr] === key ? { ...i, ...changes } : i))
    );
  }

  function onRowRemoving(e) {
    e.cancel = true;

    if (immediate) {
      if (onDeleteRow) onDeleteRow(e.data[keyExpr], e.data);
      return;
    }

    setItems((prev) => prev.filter((i) => i[keyExpr] !== e.data[keyExpr]));
    if (gridRef.current) {
      gridRef.current.instance().refresh();
    }
  }

  return (
    <div style={{ marginBottom: '12px' }}>
      {title && (
        <div style={{ fontWeight: 600, fontSize: '15px', margin: '12px 0 6px 0' }}>{title}</div>
      )}
      <SquireDataGrid
        dataSource={items}
        ref={gridRef}
        keyExpr={keyExpr}
        showBorders={true}
        columnAutoWidth={true}
        noDataText={noDataText}
        onRowInserting={onRowInserting}
        onRowUpdated={onRowUpdated}
        onRowRemoving={onRowRemoving}
        onInitNewRow={(e) => {
          if (newRowDefaults) Object.assign(e.data, newRowDefaults);
        }}
      >
        <Editing
          mode={editMode}
          allowAdding={allowAdding}
          allowUpdating={allowUpdating}
          allowDeleting={allowDeleting}
        >
          <Texts confirmDeleteMessage={confirmDeleteMessage} />
        </Editing>
        {gridColumns}
        {detailRender && (
          <MasterDetail
            enabled={true}
            autoExpandAll={autoExpandDetail}
            render={(e) =>
              detailRender(e, { updateItem: (changes) => updateItemByKey(e.key, changes) })
            }
          />
        )}
      </SquireDataGrid>
    </div>
  );
}
